import ExpandLess from "@mui/icons-material/ExpandLess";
import { styled } from "@mui/material/styles";
import ExpandMore from "@mui/icons-material/ExpandMore";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import { black } from "designSystem/styles/color";
import OverflowText from "designSystem/components/text/OverflowText";
import { HOVER } from "designSystem/theme";
import { TooltipProps } from "designSystem/components/tooltip/Tooltip";
import { DataSetupGroup, DataSetupSubsection, useDataSetupStore } from "csi/state/datasetup";
import { useStore } from "csi/state/hooks";
import { observer } from "mobx-react-lite";
import React, { ReactNode } from "react";
import DataSetupItemList from "./DataSetupItemList";
import DataSetupItemPlaceholder from "./DataSetupItemPlaceholder";

const PREFIX = "DataSetupSectionContent";

const classes = {
	group: `${PREFIX}group`,
};

const StyledBox = styled(Box)((
	{
		theme,
	},
) => ({
	[`&.${classes.group}`]: {
		color: black[500],

		[HOVER]: {
			color: black[300],
			cursor: "pointer",
		},
	},
}));

const DataSetupSectionContent = observer(() => {
	const { localStore } = useStore();
	const dataSetupStore = useDataSetupStore();
	const { activeItem, activeSection } = dataSetupStore;
	const { availableChildren } = activeSection;
	const localDataSetup = localStore.dataSetup;

	const children: ReactNode[] = [];
	const tooltipProps: Partial<TooltipProps> = { placement: "right" };

	for (const [i, child] of availableChildren.entries()) {
		if (child instanceof DataSetupSubsection) {
			const subsection = child as DataSetupSubsection;

			children.push(
				<StyledBox
					key={subsection.id}
					pb={4}
					px={4}
				>
					<OverflowText
						bold
						text={subsection.name}
						tooltipProps={tooltipProps}
					/>
				</StyledBox>,
			);
		} else {
			const group = child as DataSetupGroup;
			const { id, name } = group;

			if (name) {
				// named group, so render group and collapsable list
				const collapsed = localDataSetup.isGroupCollapsed(group.id);
				const setCollapsed = () => {
					if (collapsed) {
						localDataSetup.expandGroup(id);
					} else {
						localDataSetup.collapseGroup(id);
					}
				};

				children.push(
					<StyledBox
						alignItems="center"
						aria-label={collapsed ? "expand" : "collapse"}
						className={classes.group}
						display="flex"
						key={group.id}
						mb={4}
						onClick={setCollapsed}
						px={4}
					>
						<OverflowText
							bold
							grow
							text={name}
							tooltipProps={tooltipProps}
							variant="overline"
						/>
						<Box ml={1}>
							{collapsed ? <ExpandMore /> : <ExpandLess />}
						</Box>
					</StyledBox>,
				);

				children.push(
					<Collapse
						in={!collapsed}
						key={`${group.id}List`}
						timeout="auto"
					>
						<Box pb={4}>
							{group.placeholderId ?
								<DataSetupItemPlaceholder description={group.placeholderName} />
								:
								<DataSetupItemList items={group.items} selectedItem={activeItem} />
							}
						</Box>
					</Collapse>,
				);
			} else {
				// unnamed group, so just render item list
				children.push(
					<Box key={`${group.id}List${i}`} pb={4}>
						<DataSetupItemList items={group.items} selectedItem={activeItem} />
					</Box>,
				);
			}
		}
	}

	return (
		<Box pt={4}>
			{children}
		</Box>
	);
});

export default DataSetupSectionContent;
